/**
 * Product tab card UI: image gallery and size/add hover bar.
 * Used by product-tab, product-list, and editorial-product-grid sections.
 */
(function () {
  const CART_DRAWER_SECTIONS = "cart-drawer,cart-bubble";

  function getSectionInnerHTML(html, selector) {
    const node = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
    return node ? node.innerHTML : "";
  }

  function renderCartSections(response) {
    if (!response?.sections) return false;

    const sectionsToRender = [
      { id: "cart-drawer-toggle", section: "cart-bubble", selector: ".thb-item-count" },
      { id: "Cart-Drawer", section: "cart-drawer", selector: ".side-panel-inner" },
    ];

    let rendered = false;

    sectionsToRender.forEach((config) => {
      const root = document.getElementById(config.id);
      const sectionHtml = response.sections[config.section];
      if (!root || !sectionHtml) return;

      const target = root.querySelector(config.selector) || root;
      const innerHtml = getSectionInnerHTML(sectionHtml, config.selector);
      if (!innerHtml) return;

      target.innerHTML = innerHtml;
      rendered = true;
    });

    return rendered;
  }

  function openCartDrawer() {
    if (!window.theme?.settings?.cart_drawer) return;

    const drawer = document.getElementById("Cart-Drawer");
    if (!drawer) return;

    drawer.classList.add("active");
    document.body.classList.add("open-cc");
    document.querySelector(".click-capture")?.classList.add("active");

    if (typeof dispatchCustomEvent === "function") {
      dispatchCustomEvent("cart-drawer:open");
    } else {
      document.dispatchEvent(new CustomEvent("cart-drawer:open"));
    }
  }

  function publishCartUpdate(response) {
    if (!renderCartSections(response)) {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }

    if (typeof dispatchCustomEvent === "function") {
      dispatchCustomEvent("cart:item-added", response);
    } else {
      document.dispatchEvent(new CustomEvent("cart:item-added", { detail: response }));
    }

    openCartDrawer();
    window.MinimogEvents?.emit?.("ON_ITEM_ADDED", response);
    window.Shopify?.onItemAdded?.(response);
  }

  const DESKTOP_QUERY = "(min-width: 1068px)";
  const HOVER_QUERY = "(hover: hover) and (pointer: fine)";

  if (!customElements.get("product-tab-card-gallery")) {
    class ProductTabCardGallery extends HTMLElement {
      connectedCallback() {
        this.link = this.querySelector(".product-featured-image-link");
        this.track = this.querySelector(".product-tab-card__image-track");
        this.images = Array.from(this.querySelectorAll(".product-tab-card__image"));
        this.imageArea = this.closest(".product-featured-image");
        this.card = this.closest(".product-card");
        this.primaryImage = this.querySelector(".product-tab-card__primary-image") || this.images[0] || null;
        this.dots = Array.from(
          this.imageArea?.querySelectorAll(".product-secondary-images-nav li") || []
        );

        this.index = 0;
        this.swatchHovering = false;
        this.gallerySlideCount = this.images.length;

        this.onCardEnter = this.onCardEnter.bind(this);
        this.onCardLeave = this.onCardLeave.bind(this);
        this.onSwatchLeave = this.onSwatchLeave.bind(this);
        this.onModeChange = () => this.updateInteractionMode();
        this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
        this.hoverQuery = window.matchMedia(HOVER_QUERY);
        this.desktopQuery.addEventListener("change", this.onModeChange);
        this.hoverQuery.addEventListener("change", this.onModeChange);

        this.setupSwatches();
        this.images.forEach((img) => img.setAttribute("draggable", "false"));

        if (this.images.length > 1) {
          this.prevBtn = this.querySelector(".product-tab-card__image-btn--prev");
          this.nextBtn = this.querySelector(".product-tab-card__image-btn--next");

          [this.prevBtn, this.nextBtn].forEach((button) => {
            if (!button) return;
            button.addEventListener("mousedown", (event) => event.stopPropagation());
            button.addEventListener("touchstart", (event) => event.stopPropagation(), { passive: true });
            button.addEventListener("click", (event) => this.onNavigate(event, button === this.prevBtn ? -1 : 1));
          });
        }

        this.card?.addEventListener("mouseenter", this.onCardEnter);
        this.card?.addEventListener("mouseleave", this.onCardLeave);
        // En táctil no hay hover: el primer toque sobre la tarjeta calienta las imágenes de variante.
        this.onCardTouch = this.onCardTouch.bind(this);
        this.card?.addEventListener("touchstart", this.onCardTouch, { passive: true });

        this.goToSlide(0);
        this.updateInteractionMode();
      }

      disconnectedCallback() {
        this.card?.removeEventListener("mouseenter", this.onCardEnter);
        this.card?.removeEventListener("mouseleave", this.onCardLeave);
        this.card?.removeEventListener("touchstart", this.onCardTouch);
        this.desktopQuery?.removeEventListener("change", this.onModeChange);
        this.hoverQuery?.removeEventListener("change", this.onModeChange);
        this.swatches?.forEach((swatch) => {
          swatch.removeEventListener("mouseenter", swatch._tabSwatchEnter);
          swatch.removeEventListener("mouseleave", this.onSwatchLeave);
        });
      }

      setupSwatches() {
        this.swatches = Array.from(this.card?.querySelectorAll(".product-card-swatch") || []);
        if (!this.swatches.length) return;

        this.defaultLinkHref = this.link?.getAttribute("href") || "";
        this.defaultTitleHref = this.card?.querySelector(".product-card-title")?.getAttribute("href") || "";
        this.gallerySlideCount = this.images.length;
        this.swatchSlideByKey = new Map();
        this.swatchSlideMap = new WeakMap();

        this.swatches.forEach((swatch) => {
          const onEnter = () => this.onSwatchEnter(swatch);
          swatch._tabSwatchEnter = onEnter;
          swatch.addEventListener("mouseenter", onEnter);
          swatch.addEventListener("mouseleave", this.onSwatchLeave);
        });

        this.buildSwatchSlides();
      }

      buildSwatchSlides() {
        if (!this.track) return;

        this.slideSizesWidth = this.renderedImageWidth();
        this.swatches.forEach((swatch) => {
          const srcset = swatch.dataset.srcset;
          if (!srcset) return;

          const imageKey = this.normalizeShopifyImageUrl(this.getLargestUrlFromSrcset(srcset));
          let slideIndex = this.findSlideIndexForSrcset(srcset, this.gallerySlideCount);

          if (slideIndex < 0 && imageKey && this.swatchSlideByKey.has(imageKey)) {
            slideIndex = this.swatchSlideByKey.get(imageKey);
          } else if (slideIndex < 0) {
            slideIndex = this.createSwatchSlide(srcset, swatch);
            if (imageKey) this.swatchSlideByKey.set(imageKey, slideIndex);
          }

          this.swatchSlideMap.set(swatch, slideIndex);
        });
      }

      // Precarga de las imágenes de variante solo cuando el usuario entra en la tarjeta con un
      // puntero (hover). Antes se precargaban todas a resolución completa al cargar la página.
      preloadSwatches() {
        if (this.swatchesPreloaded || !this.swatches?.length) return;
        this.swatchesPreloaded = true;
        // La tarjeta ya es visible aquí: recalcular el ancho real una vez y sincronizar el sizes de las
        // diapositivas de swatch (pueden haberse creado con la tarjeta oculta en otra pestaña).
        this.slideSizesWidth = this.renderedImageWidth();
        const sizes = `${this.slideSizesWidth}px`;
        this.images.slice(this.gallerySlideCount).forEach((img) => {
          if (img.sizes !== sizes) img.sizes = sizes;
        });
        this.swatches.forEach((swatch) => {
          const srcset = swatch.dataset.srcset;
          if (srcset) this.preloadSrcset(srcset, sizes);
        });
      }

      onCardTouch() {
        this.preloadSwatches();
      }

      renderedImageWidth() {
        const width = this.primaryImage?.clientWidth || this.clientWidth || this.card?.clientWidth || 0;
        return width > 0 ? width : 400;
      }

      createSwatchSlide(srcset, swatch) {
        const slide = document.createElement("div");
        slide.className = "product-tab-card__slide product-tab-card__slide--swatch";

        const img = document.createElement("img");
        img.loading = "lazy"; // antes de asignar src: una <img> desconectada con src empieza a descargar al instante
        img.className = "product-tab-card__image product-tab-card__image--swatch";
        const url = this.getLargestUrlFromSrcset(srcset);
        img.src = url;
        img.srcset = srcset;
        img.width = this.primaryImage?.width || img.naturalWidth || undefined;
        img.height = this.primaryImage?.height || img.naturalHeight || undefined;
        img.sizes = `${this.slideSizesWidth || 400}px`;
        img.alt = swatch.querySelector(".visually-hidden")?.textContent?.trim() || "";
        img.decoding = "async";

        slide.appendChild(img);
        this.track.appendChild(slide);
        this.images.push(img);
        return this.images.length - 1;
      }

      preloadSrcset(srcset, sizes) {
        const preload = new Image();
        preload.sizes = sizes || `${this.slideSizesWidth || this.renderedImageWidth()}px`;
        preload.srcset = srcset;
        preload.src = this.getLargestUrlFromSrcset(srcset);
      }

      getLargestUrlFromSrcset(srcset) {
        if (!srcset) return "";
        const parts = srcset.split(",").map((part) => part.trim());
        const last = parts[parts.length - 1];
        return last ? last.split(/\s+/)[0] : "";
      }

      normalizeShopifyImageUrl(url) {
        if (!url) return "";
        const file = url.split("?")[0].split("/").pop() || "";
        return file.replace(/_\d+x\d*(@2x)?(?=\.)/i, "");
      }

      findSlideIndexForSrcset(srcset, limit = this.images.length) {
        const swatchKey = this.normalizeShopifyImageUrl(this.getLargestUrlFromSrcset(srcset));
        if (!swatchKey) return -1;

        for (let i = 0; i < limit; i++) {
          const img = this.images[i];
          const imgUrl =
            img.currentSrc ||
            img.getAttribute("src") ||
            img.getAttribute("data-src") ||
            this.getLargestUrlFromSrcset(img.getAttribute("srcset") || img.getAttribute("data-srcset") || "");
          if (this.normalizeShopifyImageUrl(imgUrl) === swatchKey) return i;
        }

        return -1;
      }

      restoreProductLinks() {
        if (this.defaultLinkHref && this.link) this.link.href = this.defaultLinkHref;
        const titleLink = this.card?.querySelector(".product-card-title");
        if (this.defaultTitleHref && titleLink) titleLink.href = this.defaultTitleHref;
      }

      restoreHoverSlide() {
        if (this.swatchHovering) return;

        const onCard = this.card?.matches(":hover");
        if (onCard && this.gallerySlideCount > 1) {
          this.goToSlide(1);
        } else {
          this.goToSlide(0);
        }
      }

      onSwatchEnter(swatch) {
        if (!this.supportsHover()) return;

        this.preloadSwatches();
        this.swatchHovering = true;
        this.swatches.forEach((item) => item.classList.toggle("active", item === swatch));

        const href = swatch.dataset.href;
        if (href) {
          if (this.link) this.link.href = href;
          const titleLink = this.card?.querySelector(".product-card-title");
          if (titleLink) titleLink.href = href;
        }

        const slideIndex = this.swatchSlideMap.get(swatch);
        if (slideIndex != null) {
          this.goToSlide(slideIndex);
        }
      }

      onSwatchLeave(event) {
        if (!this.supportsHover()) return;

        if (event?.relatedTarget?.closest?.(".product-card-swatch")) return;

        this.swatchHovering = false;
        this.swatches.forEach((item) => item.classList.remove("active"));
        this.restoreProductLinks();
        this.restoreHoverSlide();
      }

      isDesktop() {
        return this.desktopQuery.matches;
      }

      supportsHover() {
        return this.hoverQuery.matches;
      }

      updateInteractionMode() {
        if (!this.isDesktop()) {
          this.goToSlide(this.index);
          return;
        }

        this.goToSlide(0);
      }

      onCardEnter() {
        if (!this.supportsHover()) return;
        this.preloadSwatches();
        if (this.swatchHovering || this.gallerySlideCount <= 1) return;
        this.goToSlide(1);
      }

      onCardLeave() {
        if (!this.supportsHover()) return;
        this.swatchHovering = false;
        this.swatches?.forEach((item) => item.classList.remove("active"));
        this.restoreProductLinks();
        this.goToSlide(0);
      }

      onNavigate(event, direction) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const nextIndex = this.index + direction;
        const inProductTab = Boolean(this.closest(".section-product-tab"));

        if (inProductTab) {
          if (nextIndex < 0 || nextIndex >= this.images.length) return;
          this.goToSlide(nextIndex);
          return;
        }

        this.goToSlide((nextIndex + this.images.length) % this.images.length);
      }

      goToSlide(index) {
        if (!this.track) return;

        const target = this.images[index];
        if (target && target.loading === "lazy") target.loading = "eager";

        this.index = index;
        this.track.style.setProperty("--slide-index", index);

        if (this.dots.length) {
          const galleryCount = this.gallerySlideCount || this.images.length;
          this.dots.forEach((dot, i) => {
            dot.classList.toggle("active", i === index && index < galleryCount);
          });
        }

        window.lazySizes?.loader?.checkElems();
      }
    }

    customElements.define("product-tab-card-gallery", ProductTabCardGallery);
  }

  if (!customElements.get("product-tab-card-hover-bar")) {
    class ProductTabCardHoverBar extends HTMLElement {
      connectedCallback() {
        this.variants = this.readJson("[data-product-tab-variants]", []);
        const sizeIndexRaw = this.querySelector("[data-product-tab-size-index]")?.textContent;
        this.sizeIndex = sizeIndexRaw != null && sizeIndexRaw !== "" ? parseInt(sizeIndexRaw, 10) : null;
        this.selectedSize = null;
        this.sizeButtons = Array.from(this.querySelectorAll(".product-tab-card__size"));
        this.addBtn = this.querySelector(".product-tab-card__add-btn");

        this.sizeButtons.forEach((button) => {
          button.addEventListener("click", (event) => this.onSizeClick(event, button));
        });

        this.addBtn?.addEventListener("click", (event) => this.onAddClick(event));

        this.markUnavailableSizes();
      }

      readJson(selector, fallback) {
        try {
          return JSON.parse(this.querySelector(selector)?.textContent || JSON.stringify(fallback));
        } catch (error) {
          return fallback;
        }
      }

      markUnavailableSizes() {
        this.sizeButtons.forEach((button) => {
          const variant = this.findVariant(button.dataset.value);
          if (!variant?.available) {
            button.classList.add("is-disabled");
          }
        });
      }

      onSizeClick(event, button) {
        event.preventDefault();
        event.stopPropagation();

        if (button.classList.contains("is-disabled")) return;

        this.selectedSize = button.dataset.value;
        this.sizeButtons.forEach((item) => {
          item.classList.toggle("is-selected", item === button);
        });
      }

      onAddClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const variant = this.getVariantToAdd();
        if (!variant?.available || this.addBtn?.classList.contains("loading")) return;

        this.addBtn.classList.add("loading");
        this.addToCart(variant.id).finally(() => {
          this.addBtn?.classList.remove("loading");
        });
      }

      getVariantToAdd() {
        if (this.sizeIndex != null && !Number.isNaN(this.sizeIndex)) {
          if (this.selectedSize) {
            return this.findVariant(this.selectedSize);
          }

          const firstAvailable = this.sizeButtons.find((button) => !button.classList.contains("is-disabled"));
          if (firstAvailable) {
            return this.findVariant(firstAvailable.dataset.value);
          }
        }

        return this.variants.find((variant) => variant.available) || this.variants[0];
      }

      findVariant(sizeValue) {
        if (this.sizeIndex == null || Number.isNaN(this.sizeIndex)) {
          return this.variants.find((variant) => variant.available) || this.variants[0];
        }

        return (
          this.variants.find((variant) => variant.options[this.sizeIndex] === sizeValue && variant.available) ||
          this.variants.find((variant) => variant.options[this.sizeIndex] === sizeValue)
        );
      }

      addToCart(variantId) {
        const routes = window.theme?.routes || window.MinimogSettings?.routes || {};
        const addUrl = routes.cart_add_url || "/cart/add.js";
        const formData = new FormData();
        formData.append("id", variantId);
        formData.append("quantity", 1);
        formData.append("sections", CART_DRAWER_SECTIONS);
        formData.append("sections_url", window.location.pathname);

        return fetch(addUrl, {
          method: "POST",
          headers: {
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: formData,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              window.MinimogTheme?.Notification?.show?.({
                target: document.body,
                method: "appendChild",
                type: "warning",
                message: response.description,
                last: 3000,
                sticky: true,
              });
              return;
            }

            publishCartUpdate(response);
          })
          .catch((error) => {
            console.error(error);
          });
      }
    }

    customElements.define("product-tab-card-hover-bar", ProductTabCardHoverBar);
  }

  const MOBILE_QUICK_ADD_QUERY = "(max-width: 767px)";
  const mobileQuickAddQuery = window.matchMedia(MOBILE_QUICK_ADD_QUERY);

  function closeProductCardQuickAdd(exceptCard) {
    document.querySelectorAll(".product-card--product-tab.is-quick-add-open").forEach((card) => {
      if (card === exceptCard) return;
      card.classList.remove("is-quick-add-open");
      card.querySelector("[data-product-card-mobile-toggle]")?.setAttribute("aria-expanded", "false");
    });
  }

  function initProductCardMobileQuickAdd() {
    document.querySelectorAll("[data-product-card-mobile-toggle]").forEach((toggle) => {
      if (toggle.dataset.productCardMobileBound === "true") return;
      toggle.dataset.productCardMobileBound = "true";

      toggle.addEventListener("click", (event) => {
        if (!mobileQuickAddQuery.matches) return;

        event.preventDefault();
        event.stopPropagation();

        const card = toggle.closest(".product-card--product-tab");
        if (!card) return;

        const willOpen = !card.classList.contains("is-quick-add-open");
        closeProductCardQuickAdd(card);

        if (willOpen) {
          card.classList.add("is-quick-add-open");
          toggle.setAttribute("aria-expanded", "true");
        } else {
          card.classList.remove("is-quick-add-open");
          toggle.setAttribute("aria-expanded", "false");
        }
      });
    });

    document.querySelectorAll(".product-card--product-tab product-tab-card-hover-bar").forEach((bar) => {
      if (bar.dataset.productCardMobileBound === "true") return;
      bar.dataset.productCardMobileBound = "true";
      bar.addEventListener("click", (event) => event.stopPropagation());
    });
  }

  function ensureProductCardMobileQuickAddDocumentClose() {
    if (document.documentElement.dataset.productCardMobileAddDocBound === "true") return;
    document.documentElement.dataset.productCardMobileAddDocBound = "true";

    document.addEventListener("click", (event) => {
      if (event.target.closest(".product-card--product-tab.is-quick-add-open")) return;
      closeProductCardQuickAdd();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      closeProductCardQuickAdd();
    });

    mobileQuickAddQuery.addEventListener("change", (event) => {
      if (!event.matches) closeProductCardQuickAdd();
    });
  }

  function observeProductCardMobileQuickAdd() {
    initProductCardMobileQuickAdd();

    if (!document.body || document.body.dataset.productCardMobileAddObserved === "true") return;
    document.body.dataset.productCardMobileAddObserved = "true";

    new MutationObserver(() => initProductCardMobileQuickAdd()).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  ensureProductCardMobileQuickAddDocumentClose();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", observeProductCardMobileQuickAdd);
  } else {
    observeProductCardMobileQuickAdd();
  }
})();
